import React from "react";
import { Link } from "react-router-dom";
import Avatar from "@/components/Avatar";
import { Star, Clock, ShieldCheck, Heart } from "@phosphor-icons/react";
import TrustScore from "@/components/TrustScore";

const tierLabel = { new: "New seller", verified: "Verified", certified: "Certified Expert" };
const riskLabel = { low: "Low risk", medium: "Medium risk", high: "High risk" };

export default function ServiceCard({ service, onFavorite, saved }) {
  const seller = service.seller || {};
  const lowestDays = Math.min(...(service.packages?.map((p) => p.delivery_days) || [3]));
  return (
    <Link
      to={`/service/${service.id}`}
      className="mv-card-hover bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col"
      data-testid={`service-card-${service.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-gray-100">
        <img
          src={service.images?.[0] || "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800"}
          alt={service.title}
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          loading="lazy"
        />
        {service.seo_risk && (
          <span
            className={`absolute top-3 left-3 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full risk-${service.seo_risk}`}
            data-testid={`risk-badge-${service.seo_risk}`}
          >
            {riskLabel[service.seo_risk]}
          </span>
        )}
        {onFavorite && (
          <button
            onClick={(e) => { e.preventDefault(); onFavorite(service.id); }}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur flex items-center justify-center hover:bg-white transition-colors"
            data-testid={`save-${service.id}`}
          >
            <Heart size={18} weight={saved ? "fill" : "regular"} className={saved ? "text-rose-500" : "text-gray-600"} />
          </button>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2">
          <Avatar src={seller.avatar_url} name={seller.name || "Seller"} size={28} testId="card-seller-avatar" />
          <span className="text-sm font-semibold text-gray-700">{seller.name || "Seller"}</span>
          {seller.tier && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-mv-primary">
              <ShieldCheck size={12} weight="duotone" /> {tierLabel[seller.tier]}
            </span>
          )}
        </div>

        <h3 className="font-display font-bold text-base text-mv-secondary line-clamp-2 mb-2 group-hover:text-mv-primary">
          {service.title}
        </h3>

        <div className="flex items-center gap-2 text-sm mb-3 flex-wrap">
          <Star size={16} weight="fill" className="text-amber-500" />
          <span className="font-bold text-mv-secondary">{(service.avg_rating || 0).toFixed(1)}</span>
          <span className="text-gray-500">({service.review_count || 0})</span>
          <span className="ml-auto inline-flex items-center gap-1 text-gray-500 text-xs">
            <Clock size={14} /> {lowestDays}d
          </span>
        </div>

        {(seller.trust_score != null || seller.is_new_seller) && (
          <div className="mb-3">
            <TrustScore
              score={seller.trust_score}
              label={seller.trust_label}
              isNew={seller.is_new_seller}
              size="sm"
              testId={`card-trust-${service.id}`}
            />
          </div>
        )}

        <div className="mt-auto pt-3 border-t border-gray-100 flex items-baseline justify-between">
          <span className="text-xs text-gray-500">Starting at</span>
          <span className="font-display font-black text-mv-secondary text-xl">
            ${Math.round(service.starting_price)}
          </span>
        </div>
      </div>
    </Link>
  );
}
